'use client';

import Image from 'next/image';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  Users,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { submitPublicContactForm } from '@/lib/api';
import { PAGE_HERO_IMAGES } from '@/lib/marketing-images';

const industries = [
  'Fleet Management',
  'Insurance',
  'Emergency Services',
  'Farming/Livestock',
  'Logistics',
  'Other',
];

const fleetSizes = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '500+',
];

interface ContactFormData {
  fullName: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  industry: string;
  fleetSize: string;
  message: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    industry: '',
    fleetSize: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const response = await submitPublicContactForm({
        name: formData.fullName,
        email: formData.email,
        company: formData.companyName || undefined,
        phone: formData.phoneNumber || undefined,
        industry: formData.industry || undefined,
        fleetSize: formData.fleetSize || undefined,
        subject: `Website enquiry: ${formData.industry || 'General'}`,
        message: formData.message,
      });
      setSubmitResult({
        type: 'success',
        message: `${response.message} Routed to ${response.routedTo}.`,
      });
      setFormData({
        fullName: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        industry: '',
        fleetSize: '',
        message: '',
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to submit contact form.';
      setSubmitResult({
        type: 'error',
        message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ── Hero ─────────────────────────────────────── */}
      <section className="gradient-hero relative overflow-hidden py-20 lg:py-28">
        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-8">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Get in <span className="text-gradient">Touch</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            We&apos;re here to help you transform your fleet operations
          </p>
          <div className="mx-auto mt-8 max-w-4xl">
            <div className="glass-card-heavy rounded-3xl p-2 border border-white/10">
              <Image
                src={PAGE_HERO_IMAGES.contact.src}
                alt={PAGE_HERO_IMAGES.contact.alt}
                width={1600}
                height={900}
                className="h-auto w-full rounded-2xl object-cover"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact Info Cards ────────────────────────────── */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {/* Phone Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="glass-card rounded-2xl p-6 text-center flex flex-col justify-between"
            >
              <div>
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neon-cyan/10 text-neon-cyan mx-auto mb-4">
                  <Phone className="h-7 w-7" />
                </div>
                <h3 className="mb-3 text-lg font-semibold text-white">Phone Numbers</h3>
                <div className="space-y-2 text-xs text-slate-300">
                  <div>
                    <span className="block font-semibold text-white">South Africa (HQ)</span>
                    <a href="tel:+27870940710" className="hover:text-neon-cyan transition">+27 87 094 0710</a>
                    <span className="text-slate-400 block text-[11px]">078 298 8867</span>
                  </div>
                  <div className="pt-1 border-t border-white/5">
                    <span className="block font-semibold text-white">DRC Congo Branch</span>
                    <a href="tel:+243960964767" className="hover:text-neon-cyan transition">+243 960 964 767</a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Email Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="glass-card rounded-2xl p-6 text-center flex flex-col justify-between"
            >
              <div>
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neon-purple/10 text-neon-purple mx-auto mb-4">
                  <Mail className="h-7 w-7" />
                </div>
                <h3 className="mb-3 text-lg font-semibold text-white">Email Us</h3>
                <p className="text-sm font-medium text-slate-200 mb-2">
                  <a href="mailto:info@glonextrack.com" className="hover:text-neon-cyan transition">info@glonextrack.com</a>
                </p>
                <p className="text-xs text-slate-400">
                  Sales, technical support & general inquiries. Response within 24 hours.
                </p>
              </div>
            </motion.div>

            {/* SA HQ Address Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="glass-card rounded-2xl p-6 text-center flex flex-col justify-between"
            >
              <div>
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neon-magenta/10 text-neon-magenta mx-auto mb-4">
                  <MapPin className="h-7 w-7" />
                </div>
                <h3 className="mb-1 text-lg font-semibold text-white">South Africa HQ</h3>
                <span className="inline-block text-[10px] uppercase tracking-wider font-semibold text-neon-cyan mb-2">Headquarters</span>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Studio 5, Bridgewater 2,<br />
                  4 Conference Lane, Century City 7441,<br />
                  Cape Town, South Africa
                </p>
              </div>
            </motion.div>

            {/* DRC Branch Address Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="glass-card rounded-2xl p-6 text-center flex flex-col justify-between"
            >
              <div>
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400 mx-auto mb-4">
                  <MapPin className="h-7 w-7" />
                </div>
                <h3 className="mb-1 text-lg font-semibold text-white">DRC Congo Branch</h3>
                <span className="inline-block text-[10px] uppercase tracking-wider font-semibold text-emerald-400 mb-2">Regional Office</span>
                <p className="text-xs text-slate-300 leading-relaxed">
                  N°22, Avenue Toko,<br />
                  13ème rue résidentielle, Motel Fikin,<br />
                  Limete, DRC Congo
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Contact Form ────────────────────────────── */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="glass-card-heavy rounded-2xl p-8 lg:p-10"
          >
            <h2 className="mb-6 text-2xl font-bold text-white">Send us a Message</h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {submitResult ? (
                <div
                  className={`rounded-xl border px-4 py-3 text-sm ${
                    submitResult.type === 'success'
                      ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                      : 'border-rose-400/40 bg-rose-500/10 text-rose-200'
                  }`}
                >
                  {submitResult.message}
                </div>
              ) : null}

              {/* Full Name */}
              <div>
                <label
                  htmlFor="fullName"
                  className="mb-2 block text-sm text-slate-300"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-xl border border-white/10 bg-white/8 px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm text-slate-300"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-xl border border-white/10 bg-white/8 px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Company Name */}
              <div>
                <label
                  htmlFor="companyName"
                  className="mb-2 block text-sm text-slate-300"
                >
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-white/10 bg-white/8 px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                  placeholder="Enter your company name"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label
                  htmlFor="phoneNumber"
                  className="mb-2 block text-sm text-slate-300"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-white/10 bg-white/8 px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                  placeholder="+27 87 000 0000"
                />
              </div>

              {/* Industry Dropdown */}
              <div>
                <label
                  htmlFor="industry"
                  className="mb-2 block text-sm text-slate-300"
                >
                  Industry
                </label>
                <Select
                  value={formData.industry}
                  onValueChange={(value) =>
                    handleSelectChange('industry', value || '')
                  }
                >
                  <SelectTrigger className="w-full rounded-xl border border-white/10 bg-white/8 text-white placeholder:text-slate-300 focus:border-neon-cyan/50 focus:ring-2 focus:ring-neon-cyan/20">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fleet Size Dropdown */}
              <div>
                <label
                  htmlFor="fleetSize"
                  className="mb-2 block text-sm text-slate-300"
                >
                  Fleet Size
                </label>
                <Select
                  value={formData.fleetSize}
                  onValueChange={(value) =>
                    handleSelectChange('fleetSize', value || '')
                  }
                >
                  <SelectTrigger className="w-full rounded-xl border border-white/10 bg-white/8 text-white placeholder:text-slate-300 focus:border-neon-cyan/50 focus:ring-2 focus:ring-neon-cyan/20">
                    <SelectValue placeholder="Select fleet size" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {fleetSizes.map((size) => (
                      <SelectItem key={size} value={size}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm text-slate-300"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full rounded-xl border border-white/10 bg-white/8 px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all resize-none"
                  placeholder="Tell us about your fleet management needs..."
                />
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-neon w-full rounded-xl px-6 py-3.5 text-sm font-semibold flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* ── Office Locations ────────────────────────────── */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="glass-card rounded-2xl overflow-hidden p-8"
          >
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Our Regional Offices</h2>
              <p className="text-sm text-slate-400 mt-1">Visit or contact our global operations centers</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* SA HQ Card */}
              <div className="relative rounded-xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-neon-cyan/10 px-3 py-1 text-xs font-semibold text-neon-cyan border border-neon-cyan/20">
                    <MapPin className="h-3.5 w-3.5" /> South Africa HQ
                  </span>
                  <a href="tel:+27870940710" className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white transition">
                    <Phone className="h-3.5 w-3.5 text-neon-cyan" /> +27 87 094 0710
                  </a>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Cape Town Office</h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Studio 5, Bridgewater 2, 4 Conference Lane<br />
                  Century City 7441, Cape Town<br />
                  South Africa
                </p>
              </div>

              {/* DRC Branch Card */}
              <div className="relative rounded-xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400 border border-emerald-500/20">
                    <MapPin className="h-3.5 w-3.5" /> DRC Congo Branch
                  </span>
                  <a href="tel:+243960964767" className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white transition">
                    <Phone className="h-3.5 w-3.5 text-emerald-400" /> +243 960 964 767
                  </a>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Limete, Kinshasa Office</h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  N°22, Avenue Toko, 13ème rue résidentielle<br />
                  Motel Fikin, Limete<br />
                  DRC Congo
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Business Hours ────────────────────────────── */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="glass-card rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-neon-cyan/10 text-neon-cyan">
                  <Clock className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-white">Business Hours</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-sm font-medium text-white">Monday - Friday</span>
                  <span className="text-sm text-slate-300">08:00 - 17:00 (SAST)</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-sm font-medium text-white">Saturday</span>
                  <span className="text-sm text-slate-300">09:00 - 13:00</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-sm font-medium text-white">Sunday</span>
                  <span className="text-sm text-slate-300">Closed</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm font-medium text-neon-cyan">Emergency Support</span>
                  <span className="text-sm text-neon-cyan">24/7</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="glass-card rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-neon-purple/10 text-neon-purple">
                  <Users className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-white">Additional Contacts</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3 py-3 border-b border-white/10">
                  <Mail className="h-4 w-4 text-neon-cyan shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white mb-1">
                      Sales Inquiries
                    </p>
                    <p className="text-sm text-slate-300">sales@glonextrack.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 py-3 border-b border-white/10">
                  <Mail className="h-4 w-4 text-neon-purple shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white mb-1">
                      Technical Support
                    </p>
                    <p className="text-sm text-slate-300">support@glonextrack.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 py-3">
                  <Mail className="h-4 w-4 text-neon-magenta shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white mb-1">
                      Partnerships
                    </p>
                    <p className="text-sm text-slate-300">partners@glonextrack.com</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
